import { AprilSelectionPayload } from '../../services/comparaison.service';

const APRIL_INVALID_MIX_KEY = 'ADPAxxes::ADPGenerali';
const DEFAULT_APRIL_DURATION_MONTHS = 240;

export type AprilContributionType = 'Variable' | 'Constante';
export type AprilCompensationMode = 'Capital' | 'Rente' | 'Mixte';

export interface AprilRawPriceItem {
  priceType?: string;
  insured?: string;
  loan?: string;
  commission?: string;
  contributionType?: string;
  guaranteeCode?: string;
  compensationMode?: string;
  eightYearsContribution?: number;
  firstYearsContribution?: number;
  yearlyAverageRate?: number;
  taea?: number;
  product?: {
    productId?: string;
    productCode?: string;
    productTitle?: string;
  };
  contribution?: {
    startDate?: string;
    endDate?: string;
    contributionAmount?: number;
  };
  messages?: Array<{
    messageType?: string;
    messageTitle?: string;
    messageReference?: string;
  }>;
}

export interface AprilBuildOptions {
  projectDurationMonths?: number | null;
}

// =====================================================================
// MASQUAGE STATIQUE — Limites d'âge à l'adhésion par produit / garantie
// =====================================================================
// Référence d'âge utilisée pour comparer à la limite d'adhésion :
//  - 'endOfEffectiveYear' : âge atteint au 31 décembre de l'année de prise
//     d'effet (= année d'effet - année de naissance). Utilisé par les produits
//     dont les CG stipulent « au 31 décembre de l'année de prise d'effet ».
//  - 'effectiveDate' : âge réel (date anniversaire) à la date d'effet.
export type AprilAgeReference = 'effectiveDate' | 'endOfEffectiveYear';

// guaranteeCode tels qu'utilisés dans le payload April (cf. builders/april.py)
export type AprilGuaranteeCode =
  | 'Deces'
  | 'PTIA'
  | 'ITT'
  | 'IPT'
  | 'IPP'
  | 'InvaliditeProfessionsMedicales';

export interface AprilProductAgeRule {
  ageReference: AprilAgeReference;
  // Âge maximum INCLUS à l'adhésion, par garantie.
  maxAdhesionAge: Partial<Record<AprilGuaranteeCode, number>>;
  // Âge minimum INCLUS à l'adhésion, au niveau du produit (toutes garanties
  // confondues). Certains produits ne sont ouverts qu'à partir d'un âge donné
  // (ex: ADP Horizon réservé aux emprunteurs/co-emprunteurs de 45 ans et plus).
  // Tout assuré plus jeune rend le produit inéligible : APRIL bloque l'offre.
  minAdhesionAge?: number;
}

// Limites d'âge à l'adhésion (source : conditions générales APRIL).
// Seuls les produits réellement renvoyés par l'API sont déclarés. Un produit
// absent de cette table n'est jamais masqué (ex: ADPEssentiel, dont les limites
// ne sont pas documentées à ce jour).
export const APRIL_PRODUCT_AGE_LIMITS: Record<string, AprilProductAgeRule> = {
  // APRIL Assurance de Prêt Equilibre — 18 à 64 ans au 31/12 de l'année d'effet.
  ADPEquilibre: {
    ageReference: 'endOfEffectiveYear',
    maxAdhesionAge: {
      Deces: 64,
      PTIA: 64,
      ITT: 64,
      IPT: 64,
      IPP: 64,
      InvaliditeProfessionsMedicales: 64,
    },
  },
  // APRIL Assurance de Prêt Optimum + (ADPv4).
  ADPv4: {
    ageReference: 'effectiveDate',
    maxAdhesionAge: {
      Deces: 80,
      PTIA: 64,
      ITT: 64,
      IPT: 64,
      IPP: 64,
      InvaliditeProfessionsMedicales: 64,
    },
  },
  // APRIL Assurance De Prêt Horizon — réservé aux emprunteurs / co-emprunteurs
  // de 45 ans et plus (cf. Fiche Produit, « Cibles »). Tout assuré de moins de
  // 45 ans est refusé par APRIL pour ce produit.
  ADPHorizon: {
    ageReference: 'effectiveDate',
    minAdhesionAge: 45,
    maxAdhesionAge: {
      Deces: 85,
      PTIA: 70,
      ITT: 64,
      IPT: 64,
      IPP: 64,
      InvaliditeProfessionsMedicales: 64,
    },
  },
  // APRIL Assurance de Prêt Intégrale — avec sélection médicale : 64 ans
  // (la variante « sans sélection médicale » avant 60 ans est plus restrictive,
  // on retient donc la borne la plus permissive pour l'adhésion).
  ADPIntegral: {
    ageReference: 'effectiveDate',
    maxAdhesionAge: {
      Deces: 64,
      PTIA: 64,
      ITT: 64,
      IPT: 64,
      IPP: 64,
      InvaliditeProfessionsMedicales: 64,
    },
  },
};

// Catégories professionnelles pour lesquelles APRIL n'ouvre pas les garanties
// d'arrêt de travail / invalidité (ITT / IPT). Si l'une de ces garanties est
// demandée pour un assuré dans l'une de ces situations, aucun produit APRIL
// n'est proposé. Valeurs telles que définies dans `professionalCategories`
// (enum.ts) : « Etudiant/ Intermittent / Saisonnier » et « Chômage ».
export const APRIL_ITT_IPT_FORBIDDEN_CATEGORIES = new Set<string>([
  'INTERMITTENT', // Étudiant / Intermittent / Saisonnier
  'CHOMAGE', // Chômage / en recherche d'emploi
  'SANS_PROFESSION', // Sans profession / Retraité / Inactif
  'RETRAITE_CADRE', // Retraité cadre
  'RETRAITE_NONCADRE', // Retraité non cadre
]);

/**
 * Indique si une catégorie professionnelle interdit les garanties ITT/IPT
 * chez APRIL. La valeur est normalisée (trim + majuscules) avant comparaison.
 */
export function isAprilCategoryForbiddenForIttIpt(
  category?: string | null
): boolean {
  const normalized = String(category ?? '').trim().toUpperCase();
  return APRIL_ITT_IPT_FORBIDDEN_CATEGORIES.has(normalized);
}

/**
 * Détermine si un produit APRIL reste éligible pour un assuré donné, compte
 * tenu des garanties demandées (inchangées) et de son âge à l'adhésion.
 * Un produit est inéligible dès qu'une garantie demandée dépasse la limite
 * d'âge d'adhésion correspondante. Sans règle connue → éligible (pas de masquage).
 */
export function isAprilProductEligibleForGuarantees(
  productCode: string | null | undefined,
  requestedGuarantees: Iterable<string>,
  ageByReference: Partial<Record<AprilAgeReference, number | null>>
): boolean {
  if (!productCode) {
    return true;
  }

  const rule = APRIL_PRODUCT_AGE_LIMITS[productCode];
  if (!rule) {
    return true;
  }

  const age = ageByReference[rule.ageReference];
  if (age === null || age === undefined || !Number.isFinite(age)) {
    return true;
  }

  // Âge minimum à l'adhésion (produit) : un assuré trop jeune rend le produit
  // inéligible quelle que soit la garantie demandée (ex: ADP Horizon < 45 ans).
  if (typeof rule.minAdhesionAge === 'number' && age < rule.minAdhesionAge) {
    return false;
  }

  for (const guarantee of requestedGuarantees) {
    const maxAge = rule.maxAdhesionAge[guarantee as AprilGuaranteeCode];
    if (typeof maxAge === 'number' && age > maxAge) {
      return false;
    }
  }

  return true;
}

export interface AprilBusinessMessage {
  insured: string | null;
  productCode: string | null;
  productTitle: string | null;
  contributionType: AprilContributionType | null;
  priceType: string | null;
  messageType: string;
  messageTitle: string;
  messageReference: string | null;
  blocking: boolean;
  hidesProduct: boolean;
}

export interface AprilInsuredOffer {
  insured: string;
  productCode: string;
  productId?: string;
  productTitle: string;
  contributionType: AprilContributionType;
  compensationMode: AprilCompensationMode | null;
  monthlyPrice: number;
  totalContribution: number;
  totalFirstYears: number;
  totalEightYears: number;
  yearlyAverageRate: number | null;
  taea: number | null;
  startDate: string | null;
  endDate: string | null;
  details: AprilRawPriceItem[];
  hasWarnings: boolean;
}

export interface AprilDetailedOffer {
  productCode: string;
  productName: string;
  contributionType: AprilContributionType;
  compensationMode: AprilCompensationMode | null;
  monthlyPrice: number;
  totalContribution: number;
  totalFirstYears: number;
  totalEightYears: number;
  yearlyAverageRate: number | null;
  taea: number | null;
  startDate: string | null;
  endDate: string | null;
  details: AprilRawPriceItem[];
  insuredOffers: AprilInsuredOffer[];
  hasWarnings: boolean;
}

export interface AprilMixedOffer {
  mixKey: string;
  title: string;
  contributionType: AprilContributionType;
  compensationMode: AprilCompensationMode | null;
  monthlyPrice: number;
  totalContribution: number;
  totalFirstYears: number;
  totalEightYears: number;
  startDate: string | null;
  endDate: string | null;
  mainOffer: AprilInsuredOffer;
  coOffer: AprilInsuredOffer;
  mainHasWarnings: boolean;
  coHasWarnings: boolean;
  aprilSelection: AprilSelectionPayload;
}

interface AprilOfferAccumulator {
  insured: string;
  productCode: string;
  productId?: string;
  productTitle: string;
  contributionType: AprilContributionType;
  details: AprilRawPriceItem[];
  totalContribution: number;
  totalFirstYears: number;
  totalEightYears: number;
  yearlyAverageRate: number | null;
  taea: number | null;
  startDate: string | null;
  endDate: string | null;
  compensationModes: Set<AprilCompensationMode>;
}

interface AprilDetailedAccumulator {
  productCode: string;
  productName: string;
  contributionType: AprilContributionType;
  insuredOffers: AprilInsuredOffer[];
  totalContribution: number;
  totalFirstYears: number;
  totalEightYears: number;
  yearlyAverageRate: number | null;
  taea: number | null;
  startDate: string | null;
  endDate: string | null;
  details: AprilRawPriceItem[];
  compensationModes: Set<AprilCompensationMode>;
  hasWarnings: boolean;
}

interface AprilRecapRates {
  yearlyAverageRate: number | null;
  taea: number | null;
}

interface AprilOfferDiagnostics {
  detailedOfferKeys: Set<string>;
  blockingOfferKeys: Set<string>;
  warningOfferKeys: Set<string>;
  recapRatesByOffer: Map<string, AprilRecapRates>;
}

const APRIL_BLOCKING_MESSAGE_TYPES = new Set([
  'donneesinvalides',
  'donneesincoherentes',
]);

const APRIL_BLOCKING_MESSAGE_TITLE_TOKENS = [
  'donnee invalide',
  'donnees invalides',
  'donnee incoherente',
  'donnees incoherentes',
  'ineligible',
  'inelegible',
  'ineligibilite',
  'non eligible',
  'non permise',
  'non permis',
  'non autorisee',
  'non autorise',
  'pas permise',
  'pas permis',
  'pas autorisee',
  'pas autorise',
  'ne sera pas prise en compte',
  'ne sera pas pris en compte',
  'ne peut pas etre',
  'limite d age',
  'age minimum',
  'age maximum',
  'sans questionnaire medical',
  'loi lemoine',
];

export function normalizeAprilContributionType(
  value?: string | null
): AprilContributionType | null {
  if (!value) {
    return null;
  }

  const normalized = value.toString().trim().toLowerCase();
  if (normalized === 'variable') {
    return 'Variable';
  }
  if (normalized === 'constant' || normalized === 'constante') {
    return 'Constante';
  }

  return null;
}

export function normalizeAprilCompensationMode(
  value?: string | null
): AprilCompensationMode | null {
  if (!value) {
    return null;
  }

  const normalized = value.toString().trim().toLowerCase();
  if (normalized === 'capital') {
    return 'Capital';
  }
  if (normalized === 'rente') {
    return 'Rente';
  }
  if (normalized === 'mixte') {
    return 'Mixte';
  }

  return null;
}

export function buildAprilInsuredOffers(
  rawAprilData: unknown,
  options: AprilBuildOptions = {}
): AprilInsuredOffer[] {
  if (!Array.isArray(rawAprilData)) {
    return [];
  }

  const projectDurationMonths = normalizeDurationMonths(
    options.projectDurationMonths,
    DEFAULT_APRIL_DURATION_MONTHS
  );
  const diagnostics = collectAprilOfferDiagnostics(rawAprilData as AprilRawPriceItem[]);

  const offerMap = new Map<string, AprilOfferAccumulator>();

  rawAprilData.forEach((entry) => {
    const item = entry as AprilRawPriceItem;
    const insured = item.insured?.trim();
    const productCode = item.product?.productCode?.trim();
    const contributionType = normalizeAprilContributionType(item.contributionType);

    if (!insured || !productCode || !contributionType) {
      return;
    }

    const groupKey = `${insured}__${productCode}__${contributionType}`;
    if (!offerMap.has(groupKey)) {
      offerMap.set(groupKey, {
        insured,
        productCode,
        productId: item.product?.productId,
        productTitle: item.product?.productTitle || productCode,
        contributionType,
        details: [],
        totalContribution: 0,
        totalFirstYears: 0,
        totalEightYears: 0,
        yearlyAverageRate: null,
        taea: null,
        startDate: null,
        endDate: null,
        compensationModes: new Set<AprilCompensationMode>(),
      });
    }

    const offer = offerMap.get(groupKey)!;
    const incomingProductTitle = item.product?.productTitle?.trim();
    if (
      incomingProductTitle &&
      offer.productTitle === offer.productCode &&
      incomingProductTitle !== offer.productCode
    ) {
      offer.productTitle = incomingProductTitle;
    }

    if (item.priceType !== 'TarifDetaille') {
      return;
    }

    offer.details.push(item);
    const contributionAmount = toFiniteNumber(item.contribution?.contributionAmount);
    offer.totalContribution += contributionAmount;
    offer.totalFirstYears += toFiniteNumber(item.firstYearsContribution);
    offer.totalEightYears += toFiniteNumber(item.eightYearsContribution);

    if (!isFiniteNumber(offer.yearlyAverageRate) && isFiniteNumber(item.yearlyAverageRate)) {
      offer.yearlyAverageRate = item.yearlyAverageRate!;
    }
    if (!isFiniteNumber(offer.taea) && isFiniteNumber(item.taea)) {
      offer.taea = item.taea!;
    }

    const compensationMode = normalizeAprilCompensationMode(item.compensationMode);
    if (compensationMode) {
      offer.compensationModes.add(compensationMode);
    }

    if (!offer.startDate && item.contribution?.startDate) {
      offer.startDate = item.contribution.startDate;
    }
    if (!offer.endDate && item.contribution?.endDate) {
      offer.endDate = item.contribution.endDate;
    }
  });

  return Array.from(offerMap.entries())
    .filter(([offerKey, offer]) => {
      if (!offer.details.length || offer.totalContribution <= 0) {
        return false;
      }

      if (diagnostics.blockingOfferKeys.has(offerKey)) {
        return false;
      }

      return true;
    })
    .map(([offerKey, offer]) => {
      const recapRates = diagnostics.recapRatesByOffer.get(offerKey);
      return {
        insured: offer.insured,
        productCode: offer.productCode,
        productId: offer.productId,
        productTitle: offer.productTitle,
        contributionType: offer.contributionType,
        compensationMode: mergeCompensationModes(offer.compensationModes),
        monthlyPrice: offer.totalContribution / projectDurationMonths,
        totalContribution: offer.totalContribution,
        totalFirstYears: offer.totalFirstYears,
        totalEightYears: offer.totalEightYears,
        yearlyAverageRate: isFiniteNumber(recapRates?.yearlyAverageRate)
          ? recapRates!.yearlyAverageRate
          : offer.yearlyAverageRate,
        taea: isFiniteNumber(recapRates?.taea) ? recapRates!.taea : offer.taea,
        startDate: offer.startDate,
        endDate: offer.endDate,
        details: offer.details,
        hasWarnings: diagnostics.warningOfferKeys.has(offerKey),
      };
    })
    .sort((left, right) => left.monthlyPrice - right.monthlyPrice);
}

export function buildAprilDetailedOffers(
  insuredOffers: AprilInsuredOffer[]
): AprilDetailedOffer[] {
  const productMap = new Map<string, AprilDetailedAccumulator>();

  insuredOffers.forEach((offer) => {
    const groupKey = `${offer.productCode}__${offer.contributionType}`;
    if (!productMap.has(groupKey)) {
      productMap.set(groupKey, {
        productCode: offer.productCode,
        productName: offer.productTitle || offer.productCode,
        contributionType: offer.contributionType,
        insuredOffers: [],
        totalContribution: 0,
        totalFirstYears: 0,
        totalEightYears: 0,
        yearlyAverageRate: null,
        taea: null,
        startDate: null,
        endDate: null,
        details: [],
        compensationModes: new Set<AprilCompensationMode>(),
        hasWarnings: false,
      });
    }

    const product = productMap.get(groupKey)!;
    if (
      product.productName === product.productCode &&
      offer.productTitle &&
      offer.productTitle !== offer.productCode
    ) {
      product.productName = offer.productTitle;
    }

    product.insuredOffers.push(offer);
    product.totalContribution += offer.totalContribution;
    product.totalFirstYears += offer.totalFirstYears;
    product.totalEightYears += offer.totalEightYears;
    product.details.push(...offer.details);

    if (isFiniteNumber(offer.yearlyAverageRate)) {
      product.yearlyAverageRate = offer.yearlyAverageRate;
    }
    if (isFiniteNumber(offer.taea)) {
      product.taea = offer.taea;
    }
    if (!product.startDate && offer.startDate) {
      product.startDate = offer.startDate;
    }
    if (!product.endDate && offer.endDate) {
      product.endDate = offer.endDate;
    }
    if (offer.compensationMode) {
      product.compensationModes.add(offer.compensationMode);
    }
    if (offer.hasWarnings) {
      product.hasWarnings = true;
    }
  });

  return Array.from(productMap.values())
    .map((product) => {
      // Somme des mensualités de chaque assuré, déjà calculées sur la durée
      // projet commune (prêt le plus long) pour rester comparable aux autres assureurs.
      const monthlyPrice = product.insuredOffers.reduce(
        (sum, offer) => sum + offer.monthlyPrice,
        0
      );
      return {
        productCode: product.productCode,
        productName: product.productName,
        contributionType: product.contributionType,
        compensationMode: mergeCompensationModes(product.compensationModes),
        monthlyPrice,
        totalContribution: product.totalContribution,
        totalFirstYears: product.totalFirstYears,
        totalEightYears: product.totalEightYears,
        yearlyAverageRate: product.yearlyAverageRate,
        taea: product.taea,
        startDate: product.startDate,
        endDate: product.endDate,
        details: product.details,
        insuredOffers: product.insuredOffers,
        hasWarnings: product.hasWarnings,
      };
    })
    .sort((left, right) => left.monthlyPrice - right.monthlyPrice);
}

export function buildAprilBusinessMessages(rawAprilData: unknown): AprilBusinessMessage[] {
  if (!Array.isArray(rawAprilData)) {
    return [];
  }

  const diagnostics = collectAprilOfferDiagnostics(rawAprilData as AprilRawPriceItem[]);

  const messageMap = new Map<string, AprilBusinessMessage>();
  rawAprilData.forEach((entry) => {
    const item = entry as AprilRawPriceItem;
    if (!Array.isArray(item.messages) || !item.messages.length) {
      return;
    }

    const insured = normalizeAprilString(item.insured);
    const productCode = normalizeAprilString(item.product?.productCode);
    const contributionType = normalizeAprilContributionType(item.contributionType);
    const offerKey = buildAprilOfferKey(insured, productCode, contributionType);
    const hasDetailedPrice = !!offerKey && diagnostics.detailedOfferKeys.has(offerKey);
    const forceHiddenByBlocking =
      !!offerKey &&
      diagnostics.blockingOfferKeys.has(offerKey) &&
      !hasDetailedPrice;

    item.messages.forEach((message) => {
      const messageTitle = normalizeAprilString(message.messageTitle);
      if (!messageTitle) {
        return;
      }

      const messageType = normalizeAprilString(message.messageType) || 'Information';
      const messageReference = normalizeAprilString(message.messageReference);
      const blocking = isAprilBlockingMessage(messageType, messageTitle);
      const messageKey = [
        insured || '',
        productCode || '',
        contributionType || '',
        messageType,
        messageTitle,
        messageReference || '',
      ].join('__');

      if (messageMap.has(messageKey)) {
        return;
      }

      messageMap.set(messageKey, {
        insured,
        productCode,
        productTitle: normalizeAprilString(item.product?.productTitle),
        contributionType,
        priceType: normalizeAprilString(item.priceType),
        messageType,
        messageTitle,
        messageReference,
        blocking,
        hidesProduct: blocking && !hasDetailedPrice || forceHiddenByBlocking,
      });
    });
  });

  return Array.from(messageMap.values()).sort((left, right) => {
    if (left.hidesProduct !== right.hidesProduct) {
      return left.hidesProduct ? -1 : 1;
    }
    if (left.blocking !== right.blocking) {
      return left.blocking ? -1 : 1;
    }

    const leftKey = [
      left.insured || '',
      left.productCode || '',
      left.contributionType || '',
      left.messageType,
      left.messageTitle,
    ].join('__');
    const rightKey = [
      right.insured || '',
      right.productCode || '',
      right.contributionType || '',
      right.messageType,
      right.messageTitle,
    ].join('__');

    return leftKey.localeCompare(rightKey);
  });
}

export function buildAprilHeterogeneousMixOffers(
  insuredOffers: AprilInsuredOffer[]
): AprilMixedOffer[] {
  const offersByInsured = groupOffersByInsured(insuredOffers);
  const insuredIds = Array.from(offersByInsured.keys()).sort();

  const mainInsuredId = insuredIds.includes('i-1') ? 'i-1' : insuredIds[0];
  const coInsuredId = insuredIds.find((insuredId) => insuredId !== mainInsuredId);

  if (!mainInsuredId || !coInsuredId) {
    return [];
  }

  const mainOffers = offersByInsured.get(mainInsuredId) || [];
  const coOffers = offersByInsured.get(coInsuredId) || [];
  if (!mainOffers.length || !coOffers.length) {
    return [];
  }

  const mixedOffers = new Map<string, AprilMixedOffer>();
  mainOffers.forEach((mainOffer) => {
    coOffers.forEach((coOffer) => {
      if (!isAprilMixPairAllowed(mainOffer, coOffer)) {
        return;
      }
      if (mainOffer.productCode === coOffer.productCode) {
        return;
      }

      const mixedOffer = createAprilMixedOffer(mainOffer, coOffer);
      const existingMixOffer = mixedOffers.get(mixedOffer.mixKey);
      if (!existingMixOffer) {
        mixedOffers.set(mixedOffer.mixKey, mixedOffer);
        return;
      }

      if (mixedOffer.monthlyPrice < existingMixOffer.monthlyPrice) {
        mixedOffers.set(mixedOffer.mixKey, mixedOffer);
        return;
      }

      if (
        mixedOffer.monthlyPrice === existingMixOffer.monthlyPrice &&
        mixedOffer.totalContribution < existingMixOffer.totalContribution
      ) {
        mixedOffers.set(mixedOffer.mixKey, mixedOffer);
      }
    });
  });

  return Array.from(mixedOffers.values()).sort((left, right) => {
    if (left.monthlyPrice !== right.monthlyPrice) {
      return left.monthlyPrice - right.monthlyPrice;
    }
    if (left.totalContribution !== right.totalContribution) {
      return left.totalContribution - right.totalContribution;
    }
    return left.title.localeCompare(right.title);
  });
}

function createAprilMixedOffer(
  mainOffer: AprilInsuredOffer,
  coOffer: AprilInsuredOffer
): AprilMixedOffer {
  const mixKey = [
    mainOffer.productCode,
    coOffer.productCode,
  ].sort().concat(mainOffer.contributionType).join('__');

  const mainLabel = mainOffer.productTitle || mainOffer.productCode;
  const coLabel = coOffer.productTitle || coOffer.productCode;

  return {
    mixKey,
    title: `${mainLabel} + ${coLabel}`,
    contributionType: mainOffer.contributionType,
    compensationMode: mergeCompensationModes([
      mainOffer.compensationMode,
      coOffer.compensationMode,
    ]),
    monthlyPrice: mainOffer.monthlyPrice + coOffer.monthlyPrice,
    totalContribution: mainOffer.totalContribution + coOffer.totalContribution,
    totalFirstYears: mainOffer.totalFirstYears + coOffer.totalFirstYears,
    totalEightYears: mainOffer.totalEightYears + coOffer.totalEightYears,
    startDate: mainOffer.startDate || coOffer.startDate,
    endDate: mainOffer.endDate || coOffer.endDate,
    mainOffer,
    coOffer,
    mainHasWarnings: mainOffer.hasWarnings,
    coHasWarnings: coOffer.hasWarnings,
    aprilSelection: {
      mainBorrower: {
        productCode: mainOffer.productCode,
        contributionType: mainOffer.contributionType,
      },
      coBorrower: {
        productCode: coOffer.productCode,
        contributionType: coOffer.contributionType,
      },
    },
  };
}

function groupOffersByInsured(
  insuredOffers: AprilInsuredOffer[]
): Map<string, AprilInsuredOffer[]> {
  const groupedOffers = new Map<string, AprilInsuredOffer[]>();

  insuredOffers.forEach((offer) => {
    if (!groupedOffers.has(offer.insured)) {
      groupedOffers.set(offer.insured, []);
    }
    groupedOffers.get(offer.insured)!.push(offer);
  });

  return groupedOffers;
}

function isAprilMixPairAllowed(
  mainOffer: AprilInsuredOffer,
  coOffer: AprilInsuredOffer
): boolean {
  if (mainOffer.contributionType !== coOffer.contributionType) {
    return false;
  }

  const invalidMixKey = [mainOffer.productCode, coOffer.productCode]
    .sort()
    .join('::');
  return invalidMixKey !== APRIL_INVALID_MIX_KEY;
}

function isAprilBlockingMessageType(value?: string | null): boolean {
  const normalizedMessageType = normalizeAprilSemanticText(value).replace(/[^a-z]/g, '');
  return APRIL_BLOCKING_MESSAGE_TYPES.has(normalizedMessageType);
}

function isAprilWarningMessageType(value?: string | null): boolean {
  return normalizeAprilSemanticText(value) === 'information';
}

function isAprilBlockingMessage(
  messageType?: string | null,
  messageTitle?: string | null
): boolean {
  if (isAprilBlockingMessageType(messageType)) {
    return true;
  }

  const normalizedTitle = normalizeAprilSemanticText(messageTitle);
  if (!normalizedTitle) {
    return false;
  }

  return APRIL_BLOCKING_MESSAGE_TITLE_TOKENS.some((token) =>
    normalizedTitle.includes(token)
  );
}

function collectAprilOfferDiagnostics(
  rawAprilData: AprilRawPriceItem[]
): AprilOfferDiagnostics {
  const detailedOfferKeys = new Set<string>();
  const blockingOfferKeys = new Set<string>();
  const warningOfferKeys = new Set<string>();
  const recapRatesByOffer = new Map<string, AprilRecapRates>();
  const offerKeysByInsuredProduct = new Map<string, Set<string>>();

  rawAprilData.forEach((item) => {
    const offerKey = buildAprilOfferKey(
      item.insured,
      item.product?.productCode,
      item.contributionType
    );

    if (!offerKey) {
      return;
    }

    if (item.priceType === 'TarifDetaille') {
      detailedOfferKeys.add(offerKey);
    }

    if (item.priceType === 'Recapitulatif') {
      const existingRates = recapRatesByOffer.get(offerKey) || {
        yearlyAverageRate: null,
        taea: null,
      };

      if (!isFiniteNumber(existingRates.yearlyAverageRate) && isFiniteNumber(item.yearlyAverageRate)) {
        existingRates.yearlyAverageRate = item.yearlyAverageRate;
      }
      if (!isFiniteNumber(existingRates.taea) && isFiniteNumber(item.taea)) {
        existingRates.taea = item.taea;
      }

      recapRatesByOffer.set(offerKey, existingRates);
    }

    const insuredProductKey = buildAprilInsuredProductKey(
      item.insured,
      item.product?.productCode
    );
    if (!insuredProductKey) {
      return;
    }

    if (!offerKeysByInsuredProduct.has(insuredProductKey)) {
      offerKeysByInsuredProduct.set(insuredProductKey, new Set<string>());
    }
    offerKeysByInsuredProduct.get(insuredProductKey)!.add(offerKey);
  });

  rawAprilData.forEach((item) => {
    const offerKey = buildAprilOfferKey(
      item.insured,
      item.product?.productCode,
      item.contributionType
    );

    if (!Array.isArray(item.messages) || !item.messages.length) {
      return;
    }

    const insuredProductKey = buildAprilInsuredProductKey(
      item.insured,
      item.product?.productCode
    );
    const targetOfferKeys = new Set<string>();

    if (offerKey) {
      targetOfferKeys.add(offerKey);
    }

    if (insuredProductKey) {
      const productOfferKeys = offerKeysByInsuredProduct.get(insuredProductKey);
      if (productOfferKeys) {
        productOfferKeys.forEach((key) => targetOfferKeys.add(key));
      }
    }

    if (!targetOfferKeys.size) {
      return;
    }

    item.messages.forEach((message) => {
      const messageType = normalizeAprilString(message.messageType);
      const messageTitle = normalizeAprilString(message.messageTitle);

      if (isAprilBlockingMessage(messageType, messageTitle)) {
        targetOfferKeys.forEach((key) => blockingOfferKeys.add(key));
      } else if (isAprilWarningMessageType(messageType)) {
        targetOfferKeys.forEach((key) => warningOfferKeys.add(key));
      }
    });
  });

  return {
    detailedOfferKeys,
    blockingOfferKeys,
    warningOfferKeys,
    recapRatesByOffer,
  };
}

function buildAprilInsuredProductKey(
  insured?: string | null,
  productCode?: string | null
): string | null {
  const normalizedInsured = normalizeAprilString(insured);
  const normalizedProductCode = normalizeAprilString(productCode);

  if (!normalizedInsured || !normalizedProductCode) {
    return null;
  }

  return [normalizedInsured, normalizedProductCode].join('__');
}

function buildAprilOfferKey(
  insured?: string | null,
  productCode?: string | null,
  contributionType?: string | null
): string | null {
  const normalizedInsured = normalizeAprilString(insured);
  const normalizedProductCode = normalizeAprilString(productCode);
  const normalizedContributionType =
    typeof contributionType === 'string' && contributionType
      ? normalizeAprilContributionType(contributionType)
      : null;

  if (!normalizedInsured || !normalizedProductCode || !normalizedContributionType) {
    return null;
  }

  return [
    normalizedInsured,
    normalizedProductCode,
    normalizedContributionType,
  ].join('__');
}

function normalizeDurationMonths(value: unknown, fallback: number): number {
  const duration = Number(value);
  return Number.isFinite(duration) && duration > 0 ? duration : fallback;
}

function mergeCompensationModes(
  compensationModes: Iterable<AprilCompensationMode | null | undefined>
): AprilCompensationMode | null {
  const normalizedModes = Array.from(compensationModes).filter(
    (mode): mode is AprilCompensationMode => !!mode
  );

  if (!normalizedModes.length) {
    return null;
  }

  const uniqueModes = new Set(normalizedModes);
  if (uniqueModes.size === 1) {
    return normalizedModes[0];
  }

  return 'Mixte';
}

function normalizeAprilString(value?: string | null): string | null {
  if (value === null || value === undefined) {
    return null;
  }

  const normalized = String(value).trim();
  return normalized ? normalized : null;
}

function normalizeAprilSemanticText(value?: string | null): string {
  const normalized = normalizeAprilString(value);
  if (!normalized) {
    return '';
  }

  return normalized
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[’']/g, ' ')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function toFiniteNumber(value: unknown): number {
  return isFiniteNumber(value) ? Number(value) : 0;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}
